/**
 * Secure storage utility for handling sensitive data
 * Provides encryption/decryption for localStorage data
 */

class SecureStorage {
  constructor() {
    this.storageKey = 'busin_secure_';
    this.encryptionKey = this.getOrCreateEncryptionKey();
  }

  // Generate or retrieve encryption key
  getOrCreateEncryptionKey() {
    const keyName = 'busin_encrypt_key';
    let key = sessionStorage.getItem(keyName);
    
    if (!key) {
      // Generate a simple key (in production, use proper key derivation)
      key = this.generateKey();
      sessionStorage.setItem(keyName, key);
    }
    
    return key;
  }

  // Simple key generation (replace with proper implementation in production)
  generateKey() {
    return btoa(Math.random().toString(36).substr(2, 16) + Date.now().toString(36));
  }

  // Simple encryption (replace with proper encryption in production)
  encrypt(data) {
    try {
      const jsonString = JSON.stringify(data);
      const encoded = btoa(jsonString);
      
      // Simple XOR encryption (for demo purposes)
      let result = '';
      for (let i = 0; i < encoded.length; i++) {
        result += String.fromCharCode(
          encoded.charCodeAt(i) ^ this.encryptionKey.charCodeAt(i % this.encryptionKey.length)
        );
      }
      
      return btoa(result);
    } catch (error) {
      console.error('Encryption failed:', error);
      return null;
    }
  }

  // Simple decryption
  decrypt(encryptedData) {
    try {
      const decoded = atob(encryptedData);
      
      // Reverse XOR decryption
      let result = '';
      for (let i = 0; i < decoded.length; i++) {
        result += String.fromCharCode(
          decoded.charCodeAt(i) ^ this.encryptionKey.charCodeAt(i % this.encryptionKey.length)
        );
      }
      
      const jsonString = atob(result);
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Decryption failed:', error);
      return null;
    }
  }

  // Store data securely
  setItem(key, value) {
    try {
      const encryptedValue = this.encrypt(value);
      if (encryptedValue) {
        localStorage.setItem(this.storageKey + key, encryptedValue);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Secure storage set failed:', error);
      return false;
    }
  }

  // Retrieve data securely
  getItem(key) {
    try {
      const encryptedValue = localStorage.getItem(this.storageKey + key);
      if (encryptedValue) {
        return this.decrypt(encryptedValue);
      }
      return null;
    } catch (error) {
      console.error('Secure storage get failed:', error);
      return null;
    }
  }

  // Remove data
  removeItem(key) {
    try {
      localStorage.removeItem(this.storageKey + key);
      return true;
    } catch (error) {
      console.error('Secure storage remove failed:', error);
      return false;
    }
  }

  // Clear all secure data
  clear() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.storageKey)) {
          localStorage.removeItem(key);
        }
      });
      return true;
    } catch (error) {
      console.error('Secure storage clear failed:', error);
      return false;
    }
  }

  // Check if data exists
  hasItem(key) {
    return localStorage.getItem(this.storageKey + key) !== null;
  }
}

// Create and export singleton instance
const secureStorage = new SecureStorage();

// Migration helper to move existing localStorage data to secure storage
export const migrateToSecureStorage = () => {
  const itemsToMigrate = ['auth_session', 'user_preferences', 'workflow_data'];
  
  itemsToMigrate.forEach(key => {
    const existingData = localStorage.getItem(key);
    if (existingData) {
      try {
        // Try to parse as JSON first
        let parsedData;
        try {
          parsedData = JSON.parse(existingData);
        } catch {
          // If not JSON, store as string
          parsedData = existingData;
        }
        
        // Store in secure storage
        secureStorage.setItem(key, parsedData);
        
        // Remove from regular localStorage
        localStorage.removeItem(key);
        
        console.log(`Migrated ${key} to secure storage`);
      } catch (error) {
        console.error(`Failed to migrate ${key}:`, error);
      }
    }
  });
};

export default secureStorage;